import type {
  CellData,
  RowData,
  Cell,
  Column,
  ColumnDef,
} from '@tanstack/react-table';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type JSX,
  memo,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { getColumnLabel } from '../../utils/get-column-label.js';
import { DATA_TABLE_DEFAULT_HORIZONTAL_ALIGNMENT } from '../constants.js';
import {
  DataTableColumnContextProvider,
  useDataTable,
} from '../contexts/DataTableContext.js';
import * as tableStyles from '../DataTable.sty.js';
import { toPhysicalPinnedPosition } from '../features/ColumnPinning/column-pinning-utils.js';
import type { DataTableColumnType } from '../features/ColumnTypes/column-types-types.js';
import { SPACER_COLUMN } from '../features/Layout/LayoutColumnDef.js';
import { isRowDetailsColumnDef } from '../features/RowDetails/RowDetails.js';
import { isRowDragColumnDef } from '../features/RowOrder/row-order-column-utils.js';
import { isRowSelectionColumnDef } from '../features/RowSelection/RowSelection.js';
import { SubrowCellContext } from '../features/SubRows/components/SubRowCellContext.js';
import { SubRowsCellExpander } from '../features/SubRows/components/SubRowExpanders.js';
import * as thresholdStyles from '../features/Thresholds/Thresholds.sty.js';
import type { TDataTableFeatures } from '../features/types/features.types.js';
import { InlineUserActionsMenu } from '../features/UserActions/InlineUserActionsMenu.js';
import { ROW_ACTIONS_COLUMN } from '../features/UserActions/RowActionsColumnDef.js';
import { dataTableMessages } from '../messages.js';
import * as alignmentStyles from './Alignment.sty.js';
import * as cellStyles from './DataTableCell.sty.js';

interface DataTableCellProps extends StylingProps {
  cell: Cell<TDataTableFeatures, RowData, CellData>;

  /**
   * Column index in order to map it to the cells data attributes for identification.
   */
  virtualColumnIndex: number;

  /**
   * Row index in order to map it to the cells data attributes for identification.
   */
  virtualRowIndex: number;

  /**
   * Indicates whether the rendered cell is inside the drag overlay.
   */
  isInDragOverlay?: boolean;

  /**
   * Indicates whether the floating menu for the rendered cell is currently open.
   */
  hasActiveFloatingMenu?: boolean;

  /**
   * This function measures column sizes and is triggered when the cell is rendered in the DOM.
   */
  columnMeasure?: (element: Element | null) => void;

  /**
   * Node ref for row order drag and drop.
   */
  dragAndDropRef?: (element: HTMLElement | null) => void;

  /**
   * Indicates the nesting level of the row. Top level parent rows have depth 0.
   */
  subRowDepth: number;

  /**
   * Threshold pill color displayed in the first cell in a row.
   * Needs to be a valid css color definition.
   */
  thresholdPill: string | null;

  /**
   * Data for this row. Necessary for memoization to work correctly.
   * If the data changes, the cell should re-render.
   */
  rowData?: RowData;

  /**
   * Stringified cell dependencies that act as a memo invalidation for the memoized
   * component. If something on the table state changes that does not affect the
   * `cell` prop passed here, it can be added to the cellDeps `columnDef.meta` field
   * in order to use it as cache control.
   */
  cellDeps?: string;
}

interface InternalMemoizedCellProps<
  TData extends RowData,
> extends PropsWithChildren {
  /**
   * RowId from the TanStack/table cell object.
   */
  rowId: string;

  /**
   * RowIndex based on the virtualization index.
   */
  virtualRowIndex?: number;

  /**
   * ColumnId from the TanStack/table column object.
   */
  columnId: string;

  /**
   * ColumnIndex based on the virtualization index.
   */
  virtualColumnIndex: number;

  /**
   * This function measures column sizes and is triggered when the cell is rendered in the DOM.
   */
  columnMeasure?: (element: Element | null) => void;

  /**
   * Optional ref intended for drag and drop.
   */
  dragAndDropRef?: (element: HTMLElement | null) => void;

  /**
   * Determines whether the DataTable has expanding rows (subRows).
   */
  enableExpanding?: boolean;

  /**
   * Determines if the cell has a custom cell renderer defined by the column definition.
   */
  hasCustomCellRenderer?: boolean;

  /**
   * Determines if the given cell within the column contains the sub row expanding trigger.
   */
  isSubRowIndicatorColumn?: boolean;

  /**
   * Determines the column type for the given cell.
   */
  columnType?: DataTableColumnType;

  /**
   * Determines if the current cell can currently wrap its lines.
   */
  isLineWrapped?: boolean;

  /**
   * Stringified cell dependencies that act as a memo invalidation for the memoized
   * component. If something on the table state changes that does not affect the
   * `cell` prop passed here, it can be added to the cellDeps `columnDef.meta` field
   * in order to use it as cache control.
   */
  cellDeps?: string; // NOSONAR - suppress unused prop warning

  /**
   * Threshold pill color displayed in the first cell in a row.
   * Needs to be a valid css color definition.
   */
  pillColor: string | null;

  /**
   * Determines if the current cell can receive user actions and displays a menu trigger.
   */
  canCellAction?: boolean;

  /**
   * Aria label for the cell action menu trigger.
   */
  cellActionAriaLabel: string | undefined;

  /**
   * Indicates the nesting level of the row. Top level parent rows have depth 0.
   */
  subRowDepth: number;

  /**
   * Defines css positioning for the left pinned columns.
   */
  leftPosition?: string;

  /**
   * Defines css positioning for the right pinned columns.
   */
  rightPosition?: string;

  /**
   * CSS class name for the cell.
   */
  className?: string;

  /**
   * Color of the cell text, if a given threshold applies to the cell.
   * Needs to be a valid css color definition.
   */
  thresholdColor?: string;

  /**
   * Color of the cell background, if a given threshold applies to the cell.
   * Needs to be a valid css color definition.
   */
  thresholdBackgroundColor?: string;

  /**
   * Data for this row. Necessary for memoization to work correctly.
   * If the data changes, the cell should re-render.
   */
  rowData?: TData;

  /**
   * Table column definition. Necessary for memoization to work correctly.
   */
  tableColumns?: readonly ColumnDef<TDataTableFeatures, TData>[];
}

// This component should only receive primitive or memoized props in order
// to optimize render performance when committing to the DOM.
function InternalMemoizedCell<TData extends RowData>(
  props: Readonly<InternalMemoizedCellProps<TData>>,
): JSX.Element | null {
  const {
    rowId,
    virtualRowIndex,
    columnId,
    virtualColumnIndex,
    className,
    columnMeasure,
    dragAndDropRef,
    pillColor,
    enableExpanding,
    hasCustomCellRenderer,
    isSubRowIndicatorColumn,
    columnType,
    isLineWrapped,
    children,
    canCellAction,
    subRowDepth,
    leftPosition,
    rightPosition,
    cellActionAriaLabel,
    thresholdColor,
    thresholdBackgroundColor,
  } = props;

  // Handling the hovered state via React instead of via css allows us
  // to omit the button from the default render, which will reduce the
  // amount of elements rendered quite heavily.
  const [hovered, setHovered] = useState(false);
  const mergedRef = useMergeRefs<HTMLElement | null>([
    dragAndDropRef,
    columnMeasure,
  ]);

  return (
    <div
      role={columnId === SPACER_COLUMN.id ? 'presentation' : 'cell'}
      ref={mergedRef}
      data-index={`row-${virtualRowIndex}-column-${virtualColumnIndex}`}
      data-columnid={columnId}
      data-column={virtualColumnIndex}
      data-columntype={columnType}
      data-subrow-depth={subRowDepth}
      data-dt-children-name={canCellAction ? 'OpenCellActions' : undefined}
      className={className}
      onMouseEnter={() => canCellAction && setHovered(true)}
      onMouseLeave={() => canCellAction && setHovered(false)}
      style={{
        ...assignInlineVars({
          [thresholdStyles.thresholdPillColor]: pillColor,
          [cellStyles.leftPositionVar]: leftPosition,
          [cellStyles.rightPositionVar]: rightPosition,
        }),
        gridColumnStart: (virtualColumnIndex ?? 0) + 1,
        gridColumnEnd: 'span 1',
        gridRowStart: 1,
        gridRowEnd: 'span 1',
        color: thresholdColor,
        backgroundColor: thresholdBackgroundColor,
        minWidth:
          columnId === ROW_ACTIONS_COLUMN.id ? 'max-content' : undefined,
      }}
    >
      {/* Only render the subrow expander in the subrow indicator column
          and if no custom cell renderer is exposed. */}
      {enableExpanding === true &&
      !hasCustomCellRenderer &&
      isSubRowIndicatorColumn === true ? (
        <SubRowsCellExpander columnType={columnType} />
      ) : null}

      {!hasCustomCellRenderer &&
      columnType !== 'sparkline' &&
      columnType !== 'meterbar' &&
      columnType !== 'log-content' ? (
        <div
          className={clsx(
            cellStyles.innerCell({
              lineWrap: isLineWrapped,
            }),
          )}
        >
          {children}
        </div>
      ) : (
        children
      )}
      {canCellAction && (
        <InlineUserActionsMenu
          headerGroupOrRowId={rowId}
          columnId={columnId}
          type="cell"
          aria-label={cellActionAriaLabel ?? ''}
          data-column={virtualColumnIndex}
          hovered={hovered}
        />
      )}
    </div>
  );
}

/**
 * Custom prop comparison for the memoized `DataTableCell` component.
 * Compares all props except `children`, as they are not stable and would cause unnecessary re-renders.
 * If the cell has a custom renderer, it will always re-render to ensure dynamic content updates correctly.
 * @param prevProps - Previous props of the cell.
 * @param nextProps - Next props of the cell.
 * @returns boolean - true if props are equal (skip render), false otherwise (re-render).
 */
function areCellPropsEqual<TData extends RowData>(
  prevProps: Readonly<InternalMemoizedCellProps<TData>>,
  nextProps: Readonly<InternalMemoizedCellProps<TData>>,
): boolean {
  // If the cell has a custom cell renderer, always re-render, since children can always change.
  // in case it is a built-in column cellDeps are defined and we can rely on them for memoization
  if (nextProps.hasCustomCellRenderer && nextProps.cellDeps === undefined) {
    return false;
  }

  // Extract children to skip them in the comparison
  const { children: _prevChildren, ...prevRest } = prevProps;
  const { children: _nextChildren, ...nextRest } = nextProps;

  // Shallow compare all other props - if any of them changed, re-render
  const keys = Object.keys(nextRest) as Array<keyof typeof nextRest>;
  return keys.every((key) => prevRest[key] === nextRest[key]);
}

const DataTableCellMemo = memo(InternalMemoizedCell, areCellPropsEqual);

export function DataTableCell(
  props: Readonly<DataTableCellProps>,
): JSX.Element | null {
  const {
    virtualColumnIndex,
    virtualRowIndex,
    isInDragOverlay,
    dragAndDropRef,
    columnMeasure,
    hasActiveFloatingMenu,
    subRowDepth,
    thresholdPill,
    rowData,
    cellDeps,
  } = props;

  const table = useDataTable();
  const intl = useIntl();
  const subrowContext = useContext(SubrowCellContext);

  const isLineWrapped = props.cell.column.getIsLineWrapped();

  // thresholds - both row and cell highlighting
  const pillColor = virtualColumnIndex === 0 ? thresholdPill : null;
  const thresholdCellColors = props.cell.getThresholdColors();
  const columnId = props.cell.column.id;
  const columnDef = props.cell.column.columnDef;
  const hasCustomCellRenderer = columnDef.meta?.hasCustomCellRenderer;
  const canCellAction = props.cell.column.getCanCellActions();
  const cellActionAriaLabel = canCellAction
    ? intl.formatMessage(dataTableMessages.cellActionLabel, {
        columnLabel: getColumnLabel(props.cell.column),
        rowId: props.cell.row.id,
      })
    : undefined;

  const isFirstColumn = virtualColumnIndex === 0;
  const isPrefixColumn: boolean =
    isRowSelectionColumnDef(columnDef) ||
    isRowDetailsColumnDef(columnDef) ||
    isRowDragColumnDef(columnDef);

  const visualVariantConfig = table.atoms.visualVariantConfig?.get();
  const pinningState = (
    props.cell.column as unknown as Column<TDataTableFeatures, RowData>
  ).getIsPinned();
  const leftPinnedPosition =
    pinningState === 'start'
      ? props.cell.column.getLeftPinnedPosition()
      : undefined;
  const rightPinnedPosition =
    pinningState === 'end'
      ? props.cell.column.getRightPinnedPosition()
      : undefined;
  const pinnedColumnBg =
    pinningState &&
    visualVariantConfig.rowSeparation !== 'zebraStripes' &&
    (visualVariantConfig.contained ? 'contained' : 'uncontained');

  return (
    <DataTableCellMemo
      rowId={props.cell.row.id}
      virtualRowIndex={isInDragOverlay ? undefined : virtualRowIndex}
      virtualColumnIndex={virtualColumnIndex}
      columnId={columnId}
      subRowDepth={subRowDepth}
      canCellAction={canCellAction}
      columnMeasure={columnMeasure}
      dragAndDropRef={
        !isInDragOverlay && isFirstColumn ? dragAndDropRef : undefined
      }
      pillColor={pillColor}
      thresholdColor={thresholdCellColors.color}
      thresholdBackgroundColor={thresholdCellColors.backgroundColor}
      enableExpanding={table.options.enableExpanding}
      leftPosition={
        leftPinnedPosition !== undefined ? `${leftPinnedPosition}px` : undefined
      }
      rightPosition={
        rightPinnedPosition !== undefined
          ? `${rightPinnedPosition}px`
          : undefined
      }
      hasCustomCellRenderer={hasCustomCellRenderer}
      isSubRowIndicatorColumn={subrowContext.isSubRowIndicatorColumn}
      columnType={columnDef.columnType}
      isLineWrapped={isLineWrapped}
      cellActionAriaLabel={cellActionAriaLabel}
      className={clsx(
        cellStyles.outerCell({
          lineWrap: isLineWrapped,
          fontStyle: props.cell.column.getFontStyle(),
          rowDensity: hasCustomCellRenderer
            ? 'customCellRenderer'
            : visualVariantConfig.rowDensity,
          interactiveRows: table.options.enableRowInteractivity,
          verticalDividers: visualVariantConfig.verticalDividers,
          pinnedColumn: toPhysicalPinnedPosition(pinningState),
          background: pinnedColumnBg,
        }),
        alignmentStyles.horizontalAlignment[
          columnDef.alignment ?? DATA_TABLE_DEFAULT_HORIZONTAL_ALIGNMENT
        ],
        alignmentStyles.verticalAlignment[table.getBodyVerticalAlignment()],
        {
          [thresholdStyles.thresholdPill]:
            pillColor !== undefined && pillColor !== null,
          [tableStyles.prefixColumn]: isPrefixColumn,
          [tableStyles.pinnedCellInDragOverlay]:
            pinningState && isInDragOverlay,
          [cellStyles.activeInteraction]: hasActiveFloatingMenu,
        },
      )}
      rowData={rowData}
      tableColumns={table.options.columns}
      cellDeps={cellDeps}
    >
      {hasCustomCellRenderer ? (
        <DataTableColumnContextProvider value={props.cell.column}>
          <table.FlexRender cell={props.cell} />
        </DataTableColumnContextProvider>
      ) : (
        <table.FlexRender cell={props.cell} />
      )}
    </DataTableCellMemo>
  );
}
